import React, { useState, useEffect } from 'react';
import { getSale } from '../../../services/api';

const fmtS = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = s => s ? new Date(s).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) : '—';

const ESTADO_LABELS = {
  paid: 'PAGADO', credit: 'CRÉDITO', partial: 'PARCIAL',
  completed: 'COMPLETADA', cancelled: 'ANULADA',
};

// Modal genérico de solo lectura para ver el detalle de una venta desde
// cualquier módulo que muestre su documento (Cuentas por Cobrar, Cobranzas,
// Reportes) — mismo GET /api/sales/:id que ya usa VentasListado.
export default function ModalDetalleVenta({ saleId, businessId, onClose }) {
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError('');
    getSale(saleId, businessId)
      .then(res => { if (vivo) setSale(res.data?.data || null); })
      .catch(e => { if (vivo) setError(e.response?.data?.error || 'No se pudo cargar el detalle de la venta'); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [saleId, businessId]);

  const items = sale?.details || [];
  const formaPago = sale?.payments?.length
    ? [...new Set(sale.payments.map(p => p.method_name))].join(' + ')
    : (sale?.payment_status === 'credit' ? 'CRÉDITO' : '—');

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal" style={{ maxWidth: 780 }}>
        <div className="mt-modal-header">
          <span>DETALLE DE VENTA {sale?.serie ? `— ${sale.document_type} ${sale.serie}-${sale.sequence}` : ''}</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">
          {loading ? (
            <div className="mt-loading">Cargando...</div>
          ) : error ? (
            <div style={{ color: '#dc2626', padding: '20px 0', textAlign: 'center' }}>{error}</div>
          ) : !sale ? (
            <div style={{ color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>Venta no encontrada</div>
          ) : (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px',
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '12px 16px', marginBottom: 14, fontSize: 12.5,
              }}>
                <div><span style={{ color: '#64748b' }}>Fecha:</span> <b>{fmtDate(sale.sale_date)}</b></div>
                <div><span style={{ color: '#64748b' }}>Estado:</span> <b>{ESTADO_LABELS[sale.status] || sale.status}</b></div>
                <div><span style={{ color: '#64748b' }}>Cliente:</span> <b>{sale.client_name}</b></div>
                <div><span style={{ color: '#64748b' }}>Forma de Pago:</span> <b>{formaPago}</b></div>
                <div><span style={{ color: '#64748b' }}>Documento:</span> <b>{sale.client_doc_type} {sale.client_document || '—'}</b></div>
                <div><span style={{ color: '#64748b' }}>Vendedor:</span> <b>{sale.seller_name || '—'}</b></div>
              </div>

              <table className="mt-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr className="mt-head-row">
                    <th style={{ width: 32 }}>#</th>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                    <th>U.M.</th>
                    <th style={{ textAlign: 'right' }}>P. Unit.</th>
                    <th style={{ textAlign: 'right' }}>Dscto</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '14px 0' }}>Sin items</td></tr>
                  ) : items.map((item, i) => (
                    <tr key={item.id ?? i} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                      <td style={{ textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{item.product_name}</td>
                      <td style={{ textAlign: 'right' }}>{parseFloat(item.quantity_presentation ?? item.quantity ?? 0).toFixed(2)}</td>
                      <td>{item.presentation_name || item.unit_measure}</td>
                      <td style={{ textAlign: 'right' }}>{fmtS(item.unit_price_presentation ?? item.unit_price)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtS(item.discount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtS(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}></td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>SubTotal</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtS(sale.subtotal)}</td>
                  </tr>
                  {parseFloat(sale.discount) > 0 && (
                    <tr>
                      <td colSpan={5}></td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>Descuento</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>-{fmtS(sale.discount)}</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={5}></td>
                    <td style={{ textAlign: 'right', fontWeight: 800 }}>TOTAL</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#16a34a', background: '#f0fdf4' }}>{fmtS(sale.total)}</td>
                  </tr>
                </tfoot>
              </table>
              {sale.notes && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: '#64748b' }}><b>Notas:</b> {sale.notes}</div>
              )}
            </>
          )}
        </div>
        <div className="mt-modal-footer">
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
